import type { BudgetStatusData } from "@boo/shared";
import {
  getBudgetThresholdState,
  thresholdBarClass,
  thresholdTextClass,
} from "../lib/budgetThreshold";
import CategoryStatusRow from "./CategoryStatusRow";
import { useBudgetStore } from "../store/useBudgetStore";
import { CATEGORY_LABELS } from "../../monthly-summary/types/category.types";

interface Props {
  status: BudgetStatusData;
}

export default function BudgetStatusCard({ status }: Props) {
  const {
    overallBudget,
    overallSpent,
    overallRemaining,
    unallocatedBuffer,
    categories,
    untrackedCategories,
    untrackedSpend,
  } = status;
  const projections = useBudgetStore((s) => s.projections);
  const setPendingAddCategory = useBudgetStore((s) => s.setPendingAddCategory);
  const projectionMap = new Map(
    (projections?.categories ?? []).map((p) => [
      p.category,
      {
        projectedAmount: p.projectedAmount,
        monthsOfHistory: p.monthsOfHistory,
      },
    ]),
  );
  const state = getBudgetThresholdState(overallSpent, overallBudget);
  const pct =
    overallBudget > 0 ? Math.min((overallSpent / overallBudget) * 100, 100) : 0;
  const fmt = (n: number) =>
    n.toLocaleString("th-TH", { minimumFractionDigits: 0 });

  const bufferExceeded = untrackedSpend > unallocatedBuffer;
  const bufferState = bufferExceeded ? "over" : "on-track";

  return (
    <div className="rounded-2xl bg-card border border-border p-4 sm:p-6 shadow-card">
      <h2 className="text-[15px] font-semibold text-foreground mb-1">
        This Month
      </h2>
      <p className="text-[13px] text-muted-foreground mb-5">
        Spending vs budget
      </p>

      {/* Overall summary row */}
      <div className="flex items-end justify-between mb-2">
        <div>
          <p className="text-[12px] text-muted-foreground mb-0.5">Spent</p>
          <p
            className={`text-[26px] font-bold [font-variant-numeric:tabular-nums] ${thresholdTextClass(state)}`}
          >
            ฿{fmt(overallSpent)}
          </p>
        </div>
        <div className="text-right">
          <p className="text-[12px] text-muted-foreground mb-0.5">Budget</p>
          <p className="text-[18px] font-semibold text-foreground [font-variant-numeric:tabular-nums]">
            ฿{fmt(overallBudget)}
          </p>
        </div>
      </div>

      {/* Overall progress bar */}
      <div className="h-2 w-full rounded-full bg-muted overflow-hidden mb-2">
        <div
          className={`h-full rounded-full transition-all duration-300 ${thresholdBarClass(state)}`}
          style={{ width: `${pct}%` }}
        />
      </div>

      <div className="flex justify-between items-center mb-6">
        <span className="text-[12px] text-muted-foreground [font-variant-numeric:tabular-nums]">
          {pct.toFixed(0)}% used
        </span>
        <span
          className={`text-[13px] font-semibold [font-variant-numeric:tabular-nums] ${thresholdTextClass(state)}`}
        >
          {overallRemaining >= 0
            ? `฿${fmt(overallRemaining)} remaining`
            : `฿${fmt(Math.abs(overallRemaining))} over budget`}
        </span>
      </div>

      {/* Unallocated buffer + untracked-category nudge */}
      <div
        className={`rounded-xl border p-4 mb-6 ${bufferExceeded ? "border-danger-300 bg-danger-50 dark:bg-danger-900/20" : "border-border bg-muted/40"}`}
      >
        <div className="flex items-center justify-between mb-1">
          <span className="text-[13px] font-medium text-foreground">
            Unallocated buffer
          </span>
          <span
            className={`text-[14px] font-semibold [font-variant-numeric:tabular-nums] ${thresholdTextClass(bufferState)}`}
          >
            ฿{fmt(unallocatedBuffer)}
          </span>
        </div>
        {untrackedSpend > 0 && (
          <p
            className={`text-[12px] [font-variant-numeric:tabular-nums] ${bufferExceeded ? "text-danger-600 dark:text-danger-400" : "text-muted-foreground"}`}
          >
            ฿{fmt(untrackedSpend)} consumed by untracked categories
            {bufferExceeded &&
              ` — ฿${fmt(untrackedSpend - unallocatedBuffer)} over`}
          </p>
        )}
      </div>

      {/* Untracked-category nudge */}
      {untrackedCategories.length > 0 && (
        <div className="rounded-xl border border-warning-300 bg-warning-50 dark:bg-warning-900/20 p-4 mb-6">
          <p className="text-[13px] font-semibold text-warning-700 dark:text-warning-400 mb-3">
            Spending in unbudgeted categories
          </p>
          <div className="flex flex-col gap-2">
            {untrackedCategories.map((item) => (
              <div
                key={item.category}
                className="flex items-center justify-between"
              >
                <div>
                  <span className="text-[13px] text-foreground font-medium">
                    {CATEGORY_LABELS[item.category]}
                  </span>
                  <span className="text-[12px] text-muted-foreground [font-variant-numeric:tabular-nums] ml-2">
                    ฿{fmt(item.spent)} this month
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setPendingAddCategory(item.category)}
                  className="text-[12px] font-medium text-primary hover:underline"
                >
                  + Add budget
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Per-category rows */}
      {categories.length > 0 && (
        <div className="border-t border-border pt-5 flex flex-col gap-4">
          <p className="text-[12px] font-medium text-muted-foreground uppercase tracking-wide -mb-1">
            By category
          </p>
          {categories.map((cat) => {
            const proj = projectionMap.get(cat.category);
            return (
              <CategoryStatusRow
                key={cat.category}
                item={cat}
                projectedAmount={proj?.projectedAmount}
                monthsOfHistory={proj?.monthsOfHistory}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
